import React from 'react';

// Five capabilities unlocked by a single company account, then the two-way dealflow callout.
// Both live in one dark section, separated by a hairline divider, exactly as in the design.
const CAPABILITIES = [
    { num: '01', icon: '✓', title: 'Investment readiness assessment', body: 'A continuously updated score against the criteria investors actually use — so founders know exactly what to fix, before you ever have to say no.' },
    { num: '02', icon: '📁', title: 'Comprehensive diligence pack', body: 'A living data room assembling legal, corporate, IP and governance documentation automatically — the same pack that protects valuation at exit.' },
    { num: '03', icon: '📊', title: 'Financial modelling & self-diligence', body: 'Founders build and stress-test their own financial model on-platform, catching the numbers issues before they ever reach your associates.' },
    { num: '04', icon: '⌥', title: 'Software & technical self-diligence', body: 'Automated architecture, code-health and security review for software companies — surfaced to founders as a fix-list, not a rejection letter.' },
    { num: '05', icon: '⇄', title: 'Investor matching on rejection', body: 'Pass on a deal and the company doesn’t just disappear. Diligentsia matches them to other investors on the platform whose thesis fits — turning your “no” into someone else’s “yes.”', highlight: true },
];

const FLOWS = [
    { dir: 'out', tag: 'Outbound', title: 'You pass on a deal', body: 'It doesn’t disappear. It’s matched against every other investor’s thesis on the platform and routed to whoever’s actually a fit.' },
    { dir: 'in', tag: 'Inbound', title: 'Other investors pass on a deal', body: 'If it matches your criteria, it lands in your pipeline automatically — pre-qualified, pre-diligenced, ready to review.' },
];

const EcosystemSection = () => (
    <section className="lp-eco-section">
        <div className="lp-eco-inner">
            <div className="lp-eco-head">
                <span className="lp-eco-eyebrow" data-lp-reveal>Beyond deal management</span>
                <h2 data-lp-reveal data-lp-delay="60">Other platforms score a deck. We stay with the company.</h2>
                <p data-lp-reveal data-lp-delay="120">Deal triage and diligence scoring are table stakes — several vendors now offer something similar. What none of them offer is what happens next. The same Kestrel Logistics account you just clicked through doesn't stop at your decision. It keeps working.</p>
            </div>

            <div className="lp-eco-hub" data-lp-reveal>
                <span className="lp-eco-hub-pill"><span className="dot" />One Kestrel Logistics account on Diligentsia</span>
                <span className="lp-eco-hub-arrow">→</span>
                <span className="lp-eco-hub-note">unlocks all five, automatically</span>
            </div>

            <div className="lp-eco-grid">
                {CAPABILITIES.map((c, i) => (
                    <div key={c.num} className={`lp-eco-card${c.highlight ? ' is-highlight' : ''}`} data-lp-reveal data-lp-delay={i * 70}>
                        <div className="lp-eco-num">{c.num}</div>
                        <div className="lp-eco-icon" aria-hidden="true">{c.icon}</div>
                        <h4>{c.title}</h4>
                        <p>{c.body}</p>
                    </div>
                ))}
            </div>

            <p className="lp-eco-foot" data-lp-reveal>Five capabilities. One account. <span>No other platform builds this ecosystem.</span></p>

            <div className="lp-eco-divider" />

            <div className="lp-net-head">
                <span className="lp-net-eyebrow" data-lp-reveal>And it flows back to you</span>
                <h3 data-lp-reveal data-lp-delay="60">Dealflow doesn't only leave your pipeline. It arrives in it.</h3>
                <p data-lp-reveal data-lp-delay="120">Every VC and advisor on Diligentsia runs the same thesis-matching engine. When another investor passes on a company that fits your sector, stage, geography or check size, it lands in your pipeline pre-qualified and pre-diligenced — the same 360° view you just saw on Kestrel Logistics, already attached.</p>
            </div>

            <div className="lp-net-flow">
                <div className={`lp-flow-card is-${FLOWS[0]['dir']}`} data-lp-reveal>
                    <div className="lp-flow-tag">{FLOWS[0]['tag']}</div>
                    <h4>{FLOWS[0]['title']}</h4>
                    <p>{FLOWS[0]['body']}</p>
                </div>
                <div className="lp-flow-arrow" aria-hidden="true">⇄</div>
                <div className={`lp-flow-card is-${FLOWS[1]['dir']}`} data-lp-reveal data-lp-delay="100">
                    <div className="lp-flow-tag">{FLOWS[1]['tag']}</div>
                    <h4>{FLOWS[1]['title']}</h4>
                    <p>{FLOWS[1]['body']}</p>
                </div>
            </div>

            <p className="lp-net-foot" data-lp-reveal>40+ investors and advisors. <span>One shared engine, working in both directions.</span></p>
        </div>
    </section>
);

export default EcosystemSection;
